import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { DraftOrder } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';

import { OrderLink } from '@/features/orders';

import { DraftOrderStatusBadge } from './draft-order-status-badge';

/** Column model of the draft orders table; `meta` ties columns to the list's sorts and filters. */
export function useDraftOrderColumns(storeId: string) {
  const { t } = useTranslation('draftOrders');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<DraftOrder>>>(
    () => [
      {
        id: 'name',
        header: t('columns.name'),
        cell: ({ row }) => (
          <Link
            to="/s/$storeId/draft-orders/$draftId"
            params={{ storeId, draftId: row.original.id }}
            className="font-mono text-[13px] font-medium hover:underline"
            onClick={(e) => e.stopPropagation()}
          >
            {row.original.name}
          </Link>
        ),
        meta: { label: t('columns.name'), lockVisibility: true },
        size: 100,
      },
      {
        id: 'customer',
        header: t('columns.customer'),
        cell: ({ row }) =>
          row.original.email ? (
            <span className="block max-w-64 truncate">{row.original.email}</span>
          ) : (
            <span className="text-muted-foreground">{t('noEmail')}</span>
          ),
        meta: { label: t('columns.customer'), filterKey: 'customerId' },
      },
      {
        id: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <DraftOrderStatusBadge status={row.original.status} />,
        meta: { label: t('columns.status'), filterKey: 'status' },
        size: 130,
      },
      {
        id: 'total',
        header: t('columns.total'),
        cell: ({ row }) => <span className="tabular-nums">{format.money(row.original.total)}</span>,
        meta: { sortField: 'total', filterKey: 'total', align: 'end' },
        size: 120,
      },
      {
        id: 'order',
        header: t('columns.order'),
        cell: ({ row }) =>
          row.original.orderId ? (
            <OrderLink
              storeId={storeId}
              orderId={row.original.orderId}
              name={row.original.orderName}
            />
          ) : (
            <span className="text-muted-foreground">—</span>
          ),
        meta: { label: t('columns.order') },
        size: 110,
      },
      {
        id: 'updatedAt',
        header: t('columns.updated'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.updatedAt)}>
            {format.relative(row.original.updatedAt)}
          </span>
        ),
        meta: { sortField: 'updatedAt', filterKey: 'updatedAt' },
        size: 130,
      },
    ],
    [format, storeId, t],
  );
}
